import React, { useState } from "react";
import { t } from "i18next";

import { IHero, IHeroOptions, IHeroText } from "../../../interfaces/event/hero";

import InputColorPicker from "../../shared/inputColorPicker/InputColorPicker";
import PageTitle from "../../shared/pageTitle/PageTitle";
import BaseToggle from "../../shared/BaseToggle";
import Typography from "../../shared/typography/Typography";

import { Label } from "../../shared-v2/Label";
import { TextActions } from "../../shared-v2/TextActions";
import { TextArea } from "../../shared-v2/TextArea";
import { AlignActions } from "../../shared-v2/AlignActions";
import { Subtitle } from "../../shared-v2/Subtitle";
import { IFile, UploadBanner } from "../../shared-v2/UploadBanner";

import { breakpointSize } from "../../../theme/mediaQuerie";

import { Column, Row, InputGroup, RowGrid, ToggleContainer } from "./style";

interface IProps {
  hero: IHero;
  setHero: React.Dispatch<React.SetStateAction<IHero>>;
}

const isMobile = window.innerWidth <= breakpointSize.Md;

export const Hero = ({hero, setHero}: IProps) => {
  const [fileDesktop, setFileDesktop] = useState<IFile | null>(null);
  const [fileMobile, setFileMobile] = useState<IFile | null>(null);

  const updateHero = (attr: keyof IHero, value: string | boolean) => {
    setHero(prev => ({...prev, [attr]: value}));
  };

  const updateOptions = (attr: keyof IHeroOptions, value: string) => {
    setHero(prev => ({...prev, options: {...prev.options, [attr]: value}}));
  };

  const updateText = (attr: keyof IHeroText, value: string) => {
    setHero(prev => ({...prev, options: {...prev.options, text: {...prev.options.text, [attr]: value}}}));
  };

  return (
    <>

      <RowGrid>
        { !isMobile && <PageTitle style={{margin: 0}}>{t("dashboard.event.hero.title")}</PageTitle> }
        <ToggleContainer>
          <Typography type="paragraphS" color="title">{t("dashboard.event.displaySection")}</Typography>
          <BaseToggle
            initialCheck={hero.menu ?? false} 
            onChange={(checked: boolean) => updateHero("menu", checked)} 
          />
        </ToggleContainer>
      </RowGrid>

      <Column>
        <Row>
          <Subtitle>{t("title")}</Subtitle>
          <Row>
            <TextActions 
              setTextAlign={(value) => updateText("position", value)} 
              textAlign={hero.options.text.position} 
            />
            {!isMobile && (
              <AlignActions 
                setAlignX={(value) => updateOptions("positionX", value)} 
                alignX={hero.options.positionX}  
                setAlignY={(value) => updateOptions("positionY", value)} 
                alignY={hero.options.positionY}  
                />
            )}
          </Row>
        </Row>
        <TextArea 
          positionX={hero.options.positionX}  
          positionY={hero.options.positionY}
          value={hero.options.text.text}
          onChange={(e) => updateText("text", e.target.value)} 
          maxLength={50}
          textAlign={hero.options.text.position}
        />
        {isMobile && (
          <AlignActions 
            setAlignX={(value) => updateOptions("positionX", value)} 
            alignX={hero.options.positionX}  
            setAlignY={(value) => updateOptions("positionY", value)} 
            alignY={hero.options.positionY}  
            />
        )}
      </Column>

      <Subtitle>{t("backgroundImage")}</Subtitle>

      <RowGrid style={isMobile ? {gap: '16px'} : {}}>
        <UploadBanner 
          file={fileDesktop} 
          setFile={(file) => {updateOptions("backgroudImage", file?.url ?? ""); setFileDesktop(file);}} 
          maxBannerHeight={1020} 
          maxBannerWidth={1920} 
          maxBannerSize={1024 * 1024 * 10}
          type="desktop"
          id="hero-banner-desktop"
        />
        <UploadBanner 
          file={fileMobile} 
          setFile={(file) => {updateOptions("imageMobile", file?.url ?? ""); setFileMobile(file);}} 
          maxBannerHeight={360} 
          maxBannerWidth={720} 
          maxBannerSize={1024 * 1024 * 10}
          type="mobile"
          id="hero-banner-mobile"
        />
      </RowGrid>

      <InputGroup>
        <Subtitle>{t("backgroundColor")}</Subtitle>
        <InputColorPicker 
          color={hero.options.backgroudColor} 
          handleChangeColor={(e) => updateOptions("backgroudColor", e.hex)} 
        />
      </InputGroup>
    </>
  );
}